import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faBell, faLayerGroup, faPlusCircle, faStream } from '@fortawesome/free-solid-svg-icons';

import { RumMetadataSetter } from '@Analytics';
import { AuthenticationGuard, ErrorMessage, LanguageSetter, withSidebar } from '@Components';
import {
  AgentPage,
  AskBops,
  ConfigurationPolicyWrapper,
  ConfigureLink,
  Credentials,
  CredentialsSummary,
  Documentation,
  EmptyState,
  Home,
  Integrations,
  Landing,
  ManualUpload,
  Mfa,
  Monitor,
  NoAccess,
  NotFound,
  SmbUnauthorized,
  SupplierOnboarding,
  Suppliers,
  Unauthorized,
  Users,
  WrlGuide,
} from '@Pages';
import { GlobalStyle } from '@Themes';
import WorkInProgress from './Pages/WorkInProgress/workInProgress';
import { AppContainer } from './app.styles';
import config from './config';
import './app.scss';

library.add(faBell, faLayerGroup, faPlusCircle, faStream);

const App = () => {
  const CustomErrorMessage = ({ error }) => (
    <ErrorMessage internalMessage={error || 'Unknown error catched in boundary'} />
  );

  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const email = params.get('email');
  const refreshToken = params.get('refresh');

  const prodEnv = config.REACT_APP_FLAVOR === 'bops';

  return (
    <>
      <React.StrictMode>
        <GlobalStyle />
        <AppContainer>
          <ToastContainer />
          <ErrorBoundary fallbackRender={CustomErrorMessage}>
            <LanguageSetter />
            <RumMetadataSetter />
            <Routes>
              <Route
                element={
                  <AuthenticationGuard
                    component={withSidebar}
                    token={token}
                    email={email}
                    refreshToken={refreshToken}
                  />
                }>
                <Route path="landing" element={<Landing />} />
                {prodEnv && <Route path="agent/:conversationId?" element={<AgentPage />} />}
                <Route path="ask" element={<AskBops />} />
                <Route path="monitor/:monitorId?" element={<Monitor />} />

                {(config.isGs1Flavor || config.isLogycaFlavor) && <Route path="suppliers" element={<Suppliers />} />}

                {config.isGs1Flavor && (
                  <>
                    <Route path="configuration/masters" element={<ConfigureLink />} />
                    <Route path="credentials" element={<Credentials />} />
                    <Route path="credentials-summary" element={<CredentialsSummary />} />
                    <Route path="smb-unauthorized" element={<SmbUnauthorized />} />
                    <Route path="onboarding" element={<SupplierOnboarding />} />
                  </>
                )}
                {prodEnv && (
                  <>
                    <Route path="manualUpload" element={<ManualUpload />} />
                    <Route path="integrations" element={<Integrations />} />
                    <Route path="mfa" element={<Mfa />} />
                    <Route path="configuration/:type/:policyId" element={<ConfigurationPolicyWrapper />} />
                    <Route path="configuration/masters" element={<ConfigureLink />} />
                    <Route path="credentials" element={<Credentials />} />
                  </>
                )}
                {config.isLogycaFlavor && (
                  // Masters isn't implemented for Logyca yet — render a "coming
                  // soon" placeholder so the sidebar entry has a valid target.
                  <Route path="configuration/masters" element={<WorkInProgress />} />
                )}

                <Route path="documentation/database/:pageId" element={<Documentation />} />
                <Route path="documentation/:pageId" element={<Documentation />} />
                <Route path="documentation" element={<Documentation />} />
                <Route path="unauthorized" element={<Unauthorized />} />
                <Route path="users" element={<Users />} />
                <Route path="*" element={<NotFound />} />
              </Route>
              <Route path="guides/wrl" element={<WrlGuide />} />
              <Route path="no-access" element={<NoAccess />} />
              <Route path="empty-state" element={<EmptyState />} />
              <Route path="login" element={<Navigate to="/monitor" replace />} />
              <Route path="/" element={<Home />} />
            </Routes>
          </ErrorBoundary>
        </AppContainer>
      </React.StrictMode>
      ,
    </>
  );
};

export default App;
